import React, { useCallback } from 'react';

import { Icon, VStack } from '@rbilabs/universal-components';
import Clipboard from '@react-native-clipboard/clipboard';
import { Linking, Pressable } from 'react-native';
import { Typography } from 'ucl/components/text/Typography';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { toaster } from 'components/toaster/toaster';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { getSanityItemUrl } from 'utils/sanity';

import { DebugContainer, Item, ItemName } from './styled';

const getSanitizedItemId = (id: string): string => {
  if (!id.includes('$')) {
    return id;
  }
  const segments = id.split('$');
  return segments[segments.length - 1];
};

export interface IRemoveUnavailableItemsDebugProps {
  unavailableCartEntries?: ILegacyCartEntry[];
  unavailableChildrenCartEntries?: ILegacyCartEntry[];
}

interface IDebugEntryRowProps {
  item: ILegacyCartEntry;
  onOpenSanity: (item: ILegacyCartEntry) => void;
  showCopyIcon?: boolean;
}

const DebugEntryRow: React.FC<IDebugEntryRowProps> = ({
  item,
  onOpenSanity,
  showCopyIcon = false,
}) => {
  const sanitizedId = getSanitizedItemId(item._id);
  return (
    <Pressable onPress={() => onOpenSanity(item)}>
      <Item>
        <VStack flex={1} width="100%">
          <ItemName>{item.name}</ItemName>
          <Pressable
            style={showCopyIcon ? { flexDirection: 'row', alignItems: 'center' } : undefined}
            onPress={() => {
              if (!showCopyIcon) {
                toaster.neutral('Copied to clipboard');
              }
              Clipboard.setString(sanitizedId);
            }}
          >
            {showCopyIcon ? <Icon name="mi:content-copy" /> : null}
            <Typography variant="copyOne">{item._id}</Typography>
          </Pressable>
        </VStack>
      </Item>
    </Pressable>
  );
};

export const RemoveUnavailableItemsDebug: React.FC<IRemoveUnavailableItemsDebugProps> = ({
  unavailableCartEntries,
  unavailableChildrenCartEntries,
}) => {
  const enableInternalDiagnosticTools = useFlag(LaunchDarklyFlag.ENABLE_INTERNAL_DIAGNOSTIC_TOOLS);

  const handleDebugAction = useCallback((item: ILegacyCartEntry) => {
    Linking.openURL(getSanityItemUrl(getSanitizedItemId(item._id)));
  }, []);

  const hasEntries =
    (unavailableCartEntries?.length ?? 0) > 0 || (unavailableChildrenCartEntries?.length ?? 0) > 0;

  if (!enableInternalDiagnosticTools || !hasEntries) {
    return null;
  }

  return (
    <DebugContainer>
      <Typography variant="headerTwo" textAlign="center">
        Unavailable Cart Entries Debug
      </Typography>

      {unavailableCartEntries?.map((item, index) => (
        <DebugEntryRow
          key={`${item.cartId}-${index}`}
          item={item}
          onOpenSanity={handleDebugAction}
          showCopyIcon
        />
      ))}

      {unavailableChildrenCartEntries?.map((item, index) => (
        <DebugEntryRow
          key={`${item.cartId}-${index}`}
          item={item}
          onOpenSanity={handleDebugAction}
          showCopyIcon
        />
      ))}
    </DebugContainer>
  );
};
